<template>
    <csp-desktop-info-card
        v-if="formContext.isLoaded"
        :cols="12"
        :form-context="formContext"
        icon="inticon-autoclaim_document"
        :title="formContext.data.name || 'Neues firmenspezifisches Dokument'"
        @cancel-form="handleCancel"
        @save="saveFirmSpecificDocument">
        <b-row>
            <csp-desktop-info-card-element
                class="col-3"
                :form-context="formContext"
                label="Bezeichnung des Dokuments"
                property="name"
                required
                type="text"/>
            <csp-desktop-info-card-element
                class="col-3 align-content-end"
                :form-context="formContext"
                :hide-label="true"
                :label="shareWithFirmGroupLabel"
                property="shareWithFirmGroup"
                type="checkbox"/>
            <csp-desktop-info-card-element
                class="col-3 align-content-end"
                :form-context="formContext"
                :hide-label="true"
                :label="shareWithFirmAssociationLabel"
                property="shareWithFirmAssociation"
                type="checkbox"/>
            <csp-desktop-info-card-element
                class="col-3"
                :form-context="formContext"
                label="Erlaubte Dateikategorien"
                property="allowedFileCategories"
                tooltip="wenn keine Auswahl getroffen wurde, werden alle Dateikategorien dargestellt"
                type="multiselectsearchable"/>
        </b-row>
        <b-row>
            <b-col cols="9">
                <csp-desktop-info-card-element
                    :form-context="formContext"
                    label="Text"
                    property="htmlTemplate"
                    type="ckeditor"/>
            </b-col>
            <b-col cols="3">
                <csp-desktop-info-card-element
                    :form-context="formContext"
                    label="Betreff / Thema"
                    property="subject"
                    required
                    type="autogrowtextarea"/>
                <csp-desktop-info-card-element
                    :form-context="formContext"
                    label="E-Mail Empfänger Mailadresse"
                    :placeholder="mailRecipientEmailPlaceholder"
                    property="mailRecipientEmail"
                    tooltip="Platzhalter möglich"
                    type="text"/>
                <csp-desktop-info-card-element
                    :form-context="formContext"
                    label="E-Mail Empfänger Name"
                    :placeholder="mailRecipientNamePlaceholder"
                    property="mailRecipientName"
                    tooltip="Platzhalter möglich"
                    type="text"/>
                <h3 v-if="displayAdditionalMailRecipientsHeader">
                    Zusätzliche Empfänger
                </h3>
                <b-card v-for="subformIdentifier in formContext.form.getSubformIdentifiers()"
                    :key="subformIdentifier" class="info-card mb-4">
                    <b-button v-if="formContext.isInEditMode" class="float-right" size="sm" title="Empfänger entfernen"
                        @click="removeAdditionalMailRecipient(subformIdentifier)">
                        <i class="inticon-close"/>
                    </b-button>
                    <b-row>
                        <csp-desktop-info-card-element
                            class="col-3 mb-4"
                            :form-context="formContext"
                            label="Typ"
                            property="type"
                            required
                            :subform-identifier="subformIdentifier"
                            type="autocomplete"/>
                        <csp-desktop-info-card-element
                            class="col-9 mb-4"
                            :form-context="formContext"
                            label="Name"
                            property="name"
                            :subform-identifier="subformIdentifier"
                            tooltip="Platzhalter möglich"
                            type="text"/>
                        <csp-desktop-info-card-element
                            class="col-9 mb-0"
                            :form-context="formContext"
                            label="E-Mail Adresse"
                            property="mail"
                            required
                            :subform-identifier="subformIdentifier"
                            tooltip="Platzhalter möglich"
                            type="text"/>
                    </b-row>
                </b-card>
                <div v-if="formContext.isInEditMode" class="mb-4" @click="addAdditionalMailRecipient">
                    <b-button>Empfänger hinzufügen</b-button>
                </div>
                <csp-desktop-info-card-element
                    :form-context="formContext"
                    label="Signatur bereits in Template enthalten"
                    property="hasSignatureIncluded"
                    type="checkbox"/>
                <csp-desktop-info-card-element
                    :form-context="formContext"
                    label="Vermerkthema"
                    :options-loader="loadOptions('noteCodeCategories')"
                    property="noteCodeCategory"
                    type="autocomplete"/>
                <csp-desktop-info-card-element
                    :dependencies="['noteCodeCategory']"
                    :form-context="formContext"
                    label="Vermerk"
                    :options-loader="loadOptions('noteCodesRecordless', {noteCodeCategoryId: noteCodeCategory})"
                    property="noteCode"
                    type="autocomplete"/>
                <csp-desktop-info-card-element
                    :form-context="formContext"
                    label="Nachhalte-E-Mail Vorlage"
                    :options-loader="loadFollowUpDocumentOptions"
                    property="followUpDocument"
                    type="autocomplete"/>
                <csp-desktop-info-card-element
                    :form-context="formContext"
                    label="Nachhalte-E-Mail nach (Tage)"
                    property="followUpDays"
                    type="text"/>
                <!-- eslint-disable vue/max-len -->
                <csp-desktop-info-card-element
                    :form-context="formContext"
                    label="Dokument ist verfügbar für Schadenarten"
                    property="allowedClaimTypes"
                    tooltip="Wenn keine Schadenarten ausgewählt sind, steht das Dokument uneingeschränkt für alle Schadenarten zur Verfügung"
                    type="multiselectsearchable"/>
                <!-- eslint-enable vue/max-len -->
            </b-col>
            <b-col cols="4"/>
            <b-col cols="4"/>
        </b-row>

        <b-row v-if="showActionButtons" class="mt-3">
            <b-col cols="6"/>
            <b-col class="text-right" cols="6">
                <b-button v-if="showDeleteButton" @click="openDeleteConfirmationModal">
                    Dokument löschen
                </b-button>
            </b-col>
        </b-row>

        <b-modal v-model="isDeleteConfirmationModalOpen" cancel-title="Abbrechen" title="Dokument löschen"
            @ok="deleteFirmSpecificDocument">
            Wollen Sie das Dokument wirklich löschen?
        </b-modal>
    </csp-desktop-info-card>
</template>

<script>
    import FormDataMixin from '@desktop/components/mixins/FormDataMixin';
    import Container from '@services/Container';

    const container = Container.getInstance();
    const firmRepository = container.getDesktopFirmRepository();
    const flashMessenger = container.getFlashMessenger();

    export default {
        name: 'FirmSpecificDocumentBasedata',
        mixins: [FormDataMixin],
        props: {
            isCreationForm: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            const firmId = this.$route.params.firmId;
            // an ID of 0 signifies a new document
            const firmSpecificDocumentId = this.$route.params.firmSpecificDocumentId ?? 0;

            return {
                firmId,
                firmSpecificDocumentId,
                isDeleteConfirmationModalOpen: false,
            };
        },
        computed: {
            shareWithFirmAssociationLabel() {
                const firmAssociationName = this.formContext.complexFields?.shareWithFirmAssociation
                    ?.contextData?.firmAssociationName;
                return `Für Zuordnung "${firmAssociationName}" freischalten`;
            },
            shareWithFirmGroupLabel() {
                const firmGroupName = this.formContext.complexFields?.shareWithFirmGroup?.contextData?.firmGroupName;
                return `Für Firmengruppe "${firmGroupName}" freischalten`;
            },
            mailRecipientEmailPlaceholder() {
                return this.formContext.complexFields.mailRecipientEmail.contextData.placeholder;
            },
            mailRecipientNamePlaceholder() {
                return this.formContext.complexFields.mailRecipientName.contextData.placeholder;
            },
            noteCodeCategory() {
                return this.formContext.form.data.noteCodeCategory;
            },
            showActionButtons() {
                return this.showDeleteButton;
            },
            showDeleteButton() {
                return this.formContext.data.isEntityNew === false
                    && !this.formContext.isInEditMode
                    && this.formContext.permissions.canDelete === true;
            },
            indexUrl() {
                return `/de/Schaden-Manager/Firma/firm-specific-documents/firmId/${this.firmId}`;
            },
            displayAdditionalMailRecipientsHeader() {
                return this.formContext.isInEditMode
                    || this.formContext.form.getSubformIdentifiers().length > 0;
            },
        },
        methods: {
            loadFollowUpDocumentOptions() {
                return firmRepository.getFirmSpecificDocumentFollowUpOptions(
                    this.firmId,
                    this.firmSpecificDocumentId
                );
            },
            loadOptions(type, urlParameter = {}) {
                return () => {
                    return firmRepository.loadLegacySelectOptions(type, urlParameter);
                }
            },
            saveFirmSpecificDocument(formContext) {
                if (this.isCreationForm) {
                    this.saveCreate(formContext);
                } else {
                    this.saveUpdate(formContext);
                }
            },
            saveCreate(formContext) {
                const saveFunction = (objectId, postData) => {
                    return firmRepository.createFirmSpecificDocument(this.firmId, postData);
                };
                this.saveFormData(formContext, saveFunction);
            },
            saveUpdate(formContext) {
                const saveFunction = (objectId, postData) => {
                    return firmRepository.saveFirmSpecificDocument(this.firmId, this.firmSpecificDocumentId, postData);
                };
                this.saveFormData(formContext, saveFunction);
            },
            handleCancel() {
                // only relevant for creation form
                if (!this.isCreationForm) {
                    return;
                }

                window.history.back();
            },
            openDeleteConfirmationModal() {
                this.isDeleteConfirmationModalOpen = true;
            },
            deleteFirmSpecificDocument() {
                this.formContext.isLoading++;
                firmRepository.deleteFirmSpecificDocument(this.firmId, this.firmSpecificDocumentId).then(
                    () => {
                        flashMessenger.addSuccess('Dokument wurde gelöscht');
                        window.location.href = this.indexUrl;
                    },
                    () => {
                        flashMessenger.addError('Dokument konnte nicht gelöscht werden');
                    }
                ).finally(() => {
                    this.formContext.isLoading--;
                });
            },
            addAdditionalMailRecipient() {
                const loadFunction = (objectId, postData) => {
                    return firmRepository.addFirmSpecificDocumentAdditionalRecipient(
                        this.firmId, this.firmSpecificDocumentId, postData
                    );
                };
                this.reloadFormData(this.formContext, loadFunction);
            },
            removeAdditionalMailRecipient(subformIdentifier) {
                this.formContext.form.removeSubform(subformIdentifier);
            },
        },
    }
</script>
